/**
 * GalleryPurchaseModal — CC-style card/USD version (MiniCaryn)
 *
 * Card rails: saved cards (SavedCardSelector), new card (Stripe CardElement),
 * with the wallet as an OPTIONAL USD offset (not the only option).
 * Ported from CutieCaryn; reskinned to Mini blue. Pure USD ($) throughout.
 */
import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { api } from '@/services/api'
import { useToast } from '@/components/ui/ToastContainer'
import { getPaymentMethods } from '@/api/paymentMethods'
import type { PaymentMethod } from '@/types/paymentMethod'
import SavedCardSelector from '@/features/wallet/SavedCardSelector'
import ReVerifyCardBanner from '@/features/wallet/ReVerifyCardBanner'
import { usePreviousCard } from '@/hooks/usePreviousCard'
import type { GalleryImage } from '../types'

interface GalleryPurchaseModalProps {
  image: GalleryImage
  onClose: () => void
  onSuccess: () => void
}

export default function GalleryPurchaseModal({ image, onClose, onSuccess }: GalleryPurchaseModalProps) {
  const stripe = useStripe()
  const elements = useElements()
  const { showSuccess, showError } = useToast()
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Saved cards state
  const [savedCards, setSavedCards] = useState<PaymentMethod[]>([])
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)
  const [saveCard, setSaveCard] = useState(true)
  const [isLoadingCards, setIsLoadingCards] = useState(true)

  // Wallet state (optional USD offset)
  const [walletBalance, setWalletBalance] = useState<number>(0)
  const [useWallet, setUseWallet] = useState(true)

  // Display-only "re-verify your card" hint (does not affect payment logic)
  const { card: previousCard } = usePreviousCard(true)

  const price = image.price || 0
  const isFree = price === 0

  // Calculate payment amounts (USD)
  const walletAmountToUse = useWallet ? Math.min(walletBalance, price) : 0
  const cardAmountNeeded = price - walletAmountToUse
  const canPayWithWalletOnly = walletBalance >= price

  // Fetch wallet balance
  const fetchWalletBalance = async () => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) return

      const response = await fetch(`${api.baseURL}/api/wallet/balance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setWalletBalance(data.balance || 0)
      }
    } catch (err) {
      console.error('Failed to fetch wallet balance:', err)
      setWalletBalance(0)
    }
  }

  // Fetch saved payment methods and wallet balance on mount
  useEffect(() => {
    const fetchSavedCards = async () => {
      try {
        const token = localStorage.getItem('auth_token')
        if (!token) return

        const response = await getPaymentMethods(token)
        setSavedCards(response.payment_methods || [])

        // Auto-select default card if exists
        const defaultCard = response.payment_methods.find((pm: PaymentMethod) => pm.is_default)
        if (defaultCard) {
          setSelectedCardId(defaultCard.id)
        }
      } catch (err) {
        console.error('Failed to fetch saved cards:', err)
        setSavedCards([])
        setSelectedCardId(null)
      } finally {
        setIsLoadingCards(false)
      }
    }

    fetchSavedCards()
    fetchWalletBalance()
  }, [])

  // Handle FREE image unlock
  const handleFreeUnlock = async () => {
    setIsProcessing(true)
    setError(null)

    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch(`${api.baseURL}/api/gallery/images/${image.id}/purchase`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to unlock image')
      }

      showSuccess('Image unlocked!')
      onSuccess()
      onClose()
    } catch (err: any) {
      setError(err.message)
      showError(err.message)
    } finally {
      setIsProcessing(false)
    }
  }

  // Handle PAID purchase
  const handlePurchase = async () => {
    if (isFree) {
      return handleFreeUnlock()
    }

    if (!stripe) {
      setError('Payment system not ready')
      return
    }

    setIsProcessing(true)
    setError(null)

    try {
      const token = localStorage.getItem('auth_token')

      // Determine payment method
      let paymentMethodId: string | undefined

      if (selectedCardId && selectedCardId !== 'new') {
        // Use saved card
        const card = savedCards.find(c => c.id === selectedCardId)
        paymentMethodId = card?.stripe_payment_method_id
      } else if (!canPayWithWalletOnly) {
        // Need to create payment method from card element
        const cardElement = elements?.getElement(CardElement)
        if (!cardElement) {
          throw new Error('Card details not entered')
        }

        const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement
        })

        if (pmError) {
          throw new Error(pmError.message)
        }

        paymentMethodId = paymentMethod?.id
      }

      // Initiate purchase with wallet support
      const response = await fetch(`${api.baseURL}/api/gallery/images/${image.id}/purchase`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          paymentMethodId,
          useWallet,
          walletAmount: walletAmountToUse,
          saveCard: selectedCardId === 'new' && saveCard
        })
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Purchase failed')
      }

      // Handle different payment statuses
      if (result.status === 'succeeded') {
        showSuccess('Image purchased!')
        onSuccess()
        onClose()
        return
      }

      if (result.clientSecret) {
        // Need to confirm payment
        const { error: confirmError } = await stripe.confirmCardPayment(result.clientSecret)

        if (confirmError) {
          throw new Error(confirmError.message)
        }

        // Payment confirmed - wait for webhook or poll
        showSuccess('Image purchased!')
        onSuccess()
        onClose()
      }
    } catch (err: any) {
      setError(err.message)
      showError(err.message)
    } finally {
      setIsProcessing(false)
    }
  }

  const modalContent = (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-gray-900 rounded-2xl max-w-md w-full mx-4 overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">
              {isFree ? 'Unlock Free Image' : 'Purchase Image'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Image Preview - Use blurry placeholder, not actual image */}
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-center gap-4">
            <div
              className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0"
              style={{ backgroundColor: image.dominant_color || '#374151' }}
            >
              {image.urls.placeholder ? (
                <img
                  src={image.urls.placeholder}
                  alt={image.title || 'Gallery image'}
                  className="w-full h-full object-cover"
                  style={{ filter: 'blur(2px)' }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
            </div>
            <div>
              <p className="text-white font-medium">{image.title || 'Gallery Image'}</p>
              <p className={`text-2xl font-bold ${isFree ? 'text-green-400' : 'text-white'}`}>
                {isFree ? 'FREE' : `$${price.toFixed(2)}`}
              </p>
            </div>
          </div>
        </div>

        {/* Payment Section - Only for paid images */}
        {!isFree && (
          <div className="p-6 space-y-4">
            {/* Wallet Balance (optional USD offset) */}
            {walletBalance > 0 && (
              <div className="bg-gray-800 rounded-lg p-4">
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-white">
                    Use store credit
                    <span className="text-gray-400 ml-2">(${walletBalance.toFixed(2)} available)</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={useWallet}
                    onChange={(e) => setUseWallet(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-600 text-[#4AA3CC] focus:ring-[#4AA3CC]"
                  />
                </label>
                {useWallet && walletAmountToUse > 0 && (
                  <p className="text-green-400 text-sm mt-2">
                    -${walletAmountToUse.toFixed(2)} from store credit
                    {cardAmountNeeded > 0 && ` • $${cardAmountNeeded.toFixed(2)} remaining`}
                  </p>
                )}
              </div>
            )}

            {/* Card Selection - Only if card payment needed */}
            {(!useWallet || !canPayWithWalletOnly) && (
              <>
                {isLoadingCards ? (
                  <div className="flex justify-center py-4">
                    <div className="w-6 h-6 border-2 border-[#4AA3CC] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : savedCards.length > 0 ? (
                  <SavedCardSelector
                    paymentMethods={savedCards}
                    selectedId={selectedCardId}
                    onSelect={(id) => setSelectedCardId(id)}
                    onAddNew={() => setSelectedCardId('new')}
                  />
                ) : null}

                {/* New Card Form */}
                {(savedCards.length === 0 || selectedCardId === 'new') && (
                  <div className="space-y-3">
                    <ReVerifyCardBanner card={previousCard} variant="dark" />
                    <label className="block text-sm font-medium text-gray-300">
                      {previousCard?.last4 ? 'Re-verify your card' : 'Card Details'}
                    </label>
                    <div className="bg-gray-800 rounded-lg p-4">
                      <CardElement
                        options={{
                          style: {
                            base: {
                              fontSize: '16px',
                              color: '#fff',
                              '::placeholder': { color: '#6b7280' }
                            }
                          }
                        }}
                      />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={saveCard}
                        onChange={(e) => setSaveCard(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-600 text-[#4AA3CC] focus:ring-[#4AA3CC]"
                      />
                      Save card for future purchases
                    </label>
                  </div>
                )}
              </>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-500/20 border border-red-500 rounded-lg p-3 text-red-400 text-sm">
                {error}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="p-6 bg-gray-800/50">
          <button
            onClick={handlePurchase}
            disabled={isProcessing || (!isFree && !stripe)}
            className={`w-full py-3 rounded-lg font-semibold text-white transition-all ${
              isFree
                ? 'bg-green-500 hover:bg-green-600'
                : 'bg-[#4AA3CC] hover:bg-[#3B8DB3]'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isProcessing ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Processing...
              </span>
            ) : isFree ? (
              'Unlock Image'
            ) : canPayWithWalletOnly && useWallet ? (
              'Pay with Store Credit'
            ) : (
              `Pay $${(cardAmountNeeded > 0 ? cardAmountNeeded : price).toFixed(2)}`
            )}
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}
